import React, { useState, useEffect } from "react";
import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CSpinner,
  CFormCheck,
  CFormSelect,
} from "@coreui/react";
import { Link } from "react-router-dom";
import EditIcon from "../../components/icons/EditIcon";
import DeleteIcon from "../../components/icons/DeleteIcon";
import { useMutation, useQueryClient } from "react-query";
import { useToast } from "../../hooks/useToast";
import { useCookie } from "../../hooks/useCookie";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import PromptModal from "../../components/modals/PromptModal";
import { colors } from "../../config/theme";
import { DELETE_ACTIONS } from "../../config/constants";
import { STATUS_OPTIONS } from "../../helpers/constants";
import { useAuth } from "../../hooks/useAuth";
import { formatDateStringWithTime } from "../../helpers/general";

function TableBodyRowContent({
  activity,
  deletedList,
}: {
  activity: any;
  deletedList: React.Dispatch<any>;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const deleteMutation = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(`activities/${id}`, token);
  });

  const updateStatusMutation = useMutation((status: string) => {
    return apiService.MakePutRequest(
      `activities/${activity.id}/status`,
      { status },
      token,
    );
  });

  useEffect(() => {
    if (isChecked) {
      deletedList({ type: DELETE_ACTIONS.ADD, id: activity.id });
    } else {
      deletedList({ type: DELETE_ACTIONS.REMOVE, id: activity.id });
    }
  }, [isChecked, activity.id, deletedList]);

  const handleDelete = () => {
    deleteMutation.mutate(activity.id, {
      onSuccess: () => {
        toastSuccess("Activité supprimée avec succès");
        queryClient.invalidateQueries("getActivities");
      },
      onError: () => {
        toastError(general.fr.message.operationFailed);
      },
    });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    console.log(newStatus);
    if (!newStatus || newStatus === activity.status) return;

    updateStatusMutation.mutate(newStatus, {
      onSuccess: () => {
        toastSuccess("Statut mis à jour avec succès");
        queryClient.invalidateQueries("getActivities");
      },
      onError: () => {
        toastError(general.fr.message.operationFailed);
      },
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <span className="badge bg-success">Approuvé</span>;
      case "draft":
        return <span className="badge bg-black">Brouillon</span>;
      case "pending_review":
        return <span className="badge bg-warning text-dark">En attente</span>;
      case "rejected":
        return <span className="badge bg-danger">Rejeté</span>;
      default:
        return <span className="badge bg-light text-dark">{status}</span>;
    }
  };
  const getTranslationsLanguages = () => {
    return activity.translations?.map((translation: any) => {
      return (
        <span className="badge bg-light text-black" key={translation.lang}>
          {translation.lang}
        </span>
      );
    });
  };

  return (
    <CTableRow
      style={{ borderLeft: isChecked ? `3px solid ${colors.primary}` : "" }}
    >
      <CTableDataCell className="p-3">
        <CFormCheck
          defaultChecked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
        <Link to={`/activities/update/${activity.id}`} className="ms-2 fw-bold">
          {activity.title || "Sans titre"}
        </Link>
      </CTableDataCell>
      <CTableDataCell className="p-3">
        {formatDateStringWithTime(activity.createdAt)}
      </CTableDataCell>
      <CTableDataCell className="p-3 ">
        {activity.place?.name || "Sans établissement"}
      </CTableDataCell>
      <CTableDataCell className="p-3">
        {getStatusBadge(activity.status)}
      </CTableDataCell>
      <CTableDataCell className="p-3   gap-2">
        {getTranslationsLanguages()}
      </CTableDataCell>
      <CTableDataCell className="p-3 text-muted">
        {activity.order || "Sans ordre"}
      </CTableDataCell>

      <CTableDataCell className="p-3 text-end">
        <div className="d-flex align-items-center justify-content-end gap-3">
          {isAdmin &&
            (updateStatusMutation.isLoading ? (
              <CSpinner size="sm" />
            ) : (
              <CFormSelect
                size="sm"
                style={{ width: "115px" }}
                defaultValue={activity.status}
                onChange={handleStatusChange}
                disabled={deleteMutation.isLoading}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </CFormSelect>
            ))}

          <Link to={`/activities/update/${activity.id}`}>
            <EditIcon width="18" height="18" fill={colors.secondary} />
          </Link>
          <button
            className="outline-none border-0 bg-transparent p-0"
            onClick={() => setShowModal(true)}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="20" height="20" fill={colors.primary} />
            )}
          </button>
        </div>
      </CTableDataCell>
      <PromptModal
        open={showModal}
        onAccept={handleDelete}
        onClose={() => setShowModal(false)}
      />
    </CTableRow>
  );
}

export default function ActivitiesTable({
  activities,
  onDeleteSelect,
}: {
  activities: any[];
  onDeleteSelect: React.Dispatch<any>;
}) {
  return (
    <div className="mt-3">
      <CTable responsive>
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col">Titre</CTableHeaderCell>
            <CTableHeaderCell scope="col">Date de creation</CTableHeaderCell>
            <CTableHeaderCell scope="col">Établissement</CTableHeaderCell>
            <CTableHeaderCell scope="col">Statut</CTableHeaderCell>
            <CTableHeaderCell scope="col">Translations</CTableHeaderCell>
            <CTableHeaderCell scope="col"> Ordre</CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-end">
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {activities?.map((activity) => (
            <TableBodyRowContent
              key={activity.id}
              activity={activity}
              deletedList={onDeleteSelect}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
